import {
	type DisplayModePreference,
	resolveIsWideScreen,
	thresholdForDesktopAt,
	thresholdForMobileAt
} from "~/helpers/displayModePreference";

export type ScreenOrientation = "landscape" | "portrait";

export interface MockupSize {
	heightPx: number;
	widthPx: number;
}

export interface OrientationPreview {
	isCurrent: boolean;
	isWideScreen: boolean;
	mockup: MockupSize;
	orientation: ScreenOrientation;
	viewportWidthPx: number;
}

export interface OrientationPreviews {
	landscape: OrientationPreview;
	portrait: OrientationPreview;
}

interface DescribeOrientationPreviewsParams {
	currentOrientation: ScreenOrientation;
	defaultThreshold: number;
	maxLongEdgePx: number;
	preference: DisplayModePreference;
	viewportHeight: number;
	viewportWidth: number;
}

export const orientationFromViewport = (viewportWidth: number, viewportHeight: number): ScreenOrientation =>
	viewportWidth >= viewportHeight ? "landscape" : "portrait";

export const orientationViewportWidths = (
	viewportWidth: number,
	viewportHeight: number
): { landscape: number; portrait: number } => ({
	landscape: Math.max(viewportWidth, viewportHeight),
	portrait: Math.min(viewportWidth, viewportHeight)
});

export const scaleDeviceMockups = (
	viewportWidth: number,
	viewportHeight: number,
	maxLongEdgePx: number
): { landscape: MockupSize; portrait: MockupSize } => {
	const shortEdge = Math.min(viewportWidth, viewportHeight);
	const longEdge = Math.max(viewportWidth, viewportHeight);
	const longPx = Math.round(maxLongEdgePx);
	const shortPx = longEdge > 0 ? Math.round((shortEdge / longEdge) * maxLongEdgePx) : longPx;
	return {
		landscape: { heightPx: shortPx, widthPx: longPx },
		portrait: { heightPx: longPx, widthPx: shortPx }
	};
};

export const toggleOrientationThreshold = (orientationWidthPx: number, currentlyWideScreen: boolean): number =>
	currentlyWideScreen ? thresholdForMobileAt(orientationWidthPx) : thresholdForDesktopAt(orientationWidthPx);

export const describeOrientationPreviews = ({
	currentOrientation,
	defaultThreshold,
	maxLongEdgePx,
	preference,
	viewportHeight,
	viewportWidth
}: DescribeOrientationPreviewsParams): OrientationPreviews => {
	const widths = orientationViewportWidths(viewportWidth, viewportHeight);
	const mockups = scaleDeviceMockups(viewportWidth, viewportHeight, maxLongEdgePx);
	return {
		landscape: {
			isCurrent: currentOrientation === "landscape",
			isWideScreen: resolveIsWideScreen(widths.landscape, preference, defaultThreshold),
			mockup: mockups.landscape,
			orientation: "landscape",
			viewportWidthPx: widths.landscape
		},
		portrait: {
			isCurrent: currentOrientation === "portrait",
			isWideScreen: resolveIsWideScreen(widths.portrait, preference, defaultThreshold),
			mockup: mockups.portrait,
			orientation: "portrait",
			viewportWidthPx: widths.portrait
		}
	};
};
